// @ts-nocheck
import React, { useMemo } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { useState } from '@hookstate/core';
import { Link } from 'react-router-dom';
import { AxiosError, AxiosResponse } from 'axios';
import { $clone } from '../../../../vodea/utilities';
import { showToast } from '../../../../vodea/utilities/helpers/global';
import useIsMounted from '../../../../vodea/utilities/hooks/useIsMounted';
import useScroll from '../../../../vodea/utilities/hooks/useScroll';
import DataTableInfiniteScroll, { ITableColumn } from '../../../../vodea/@vodea-ui/components/DataTableInfiniteScroll';
import VuiNumberFormat from '../../../../vodea/@vodea-ui/components/VuiNumberFormat';
import VuiSelect from '../../../../vodea/@vodea-ui/components/Forms/VuiSelect';
import ProductRepository from '../../../../repositories/ProductRepository';
import ProductMutationLogRepository from '../../../../repositories/ProductMutationLogRepository';

const transactionTypeOptions = [
  { id: 'App\\Models\\SalesOrder', name: 'Sales Order', label: 'Sales Order', url: '/sales/sales-order/' },
  { id: 'App\\Models\\GoodsReceipt', name: 'Goods Receipt', label: 'Goods Receipt', url: '/purchase/receive-item/' },
  { id: 'App\\Models\\SalesReturn', name: 'Sales Return', label: 'Sales Return', url: '/sales/return-item/' },
  { id: 'App\\Models\\StockOpname', name: 'Stock Opname', label: 'Stock Opname', url: '/inventory/stock-opname/' }
];

const transactionTypeLabel = (value: string) => {
  const found = _.find(transactionTypeOptions, option => option.id === value);
  return found ? found.name : (value || '').split('\\').pop();
};

const transactionTypeUrl = (value: string) => _.get(_.find(transactionTypeOptions, option => option.id === value), 'url');

type Props = {
  defaultParams: any;
};

const TableProductLog = ({ defaultParams }: Props) => {
  const isMounted = useIsMounted();
  const { isBottom } = useScroll();
  const [hasMore, setHasMore] = React.useState<boolean>(false);
  const [isFetchingMoreData, setIsFetchingMoreData] = React.useState<boolean>(false);
  const [hasInitialize, setHasInitialize] = React.useState<boolean>(false);
  const [totalData, setTotalData] = React.useState<number>(0);

  const [selectedProduct, setSelectedProduct] = React.useState<any>(null);
  const [selectedType, setSelectedType] = React.useState<any>(null);

  const tableConfig = useState({
    page: 1,
    per_page: 10,
    sorted_by: 'desc',
    order_by: 'created_at',
    loading: false
  });

  const tableData = useState<any[]>([]);

  const tableColumns: ITableColumn[] = [
    {
      dataField: 'product_name',
      text: 'Product'
    },
    {
      dataField: 'transaction_type',
      text: 'Type',
      render: (cell: any) => <div>{transactionTypeLabel(cell)}</div>
    },
    {
      dataField: 'transaction_number',
      text: 'Number',
      render: (cell: any, row: any) => {
        const url = transactionTypeUrl(row.transaction_type);
        if (url && row.transaction_id) {
          return (
            <Link target='_blank' className={'text-primary'} to={`${url}${row.transaction_id}`}>
              {cell}
            </Link>
          );
        }
        return <div>{cell}</div>;
      }
    },
    {
      dataField: 'created_at',
      text: 'Date',
      render: (cell: any) => <div>{cell ? moment(cell).format('DD MMM YYYY HH:mm') : '-'}</div>
    },
    {
      dataField: 'qty_before',
      text: 'Qty Before',
      render: (cell: any, row: any) => <VuiNumberFormat decimalScale={2} data={cell} suffix={row.unit_name ? ` ${row.unit_name}` : ''} />
    },
    {
      dataField: 'qty_change',
      text: 'Qty Change',
      render: (cell: any, row: any) => (
        <div style={{ color: row.type === 'IN' ? '#22c55e' : row.type === 'ADJUSTMENT' ? '#eab308' : '#ef4444', fontWeight: 600 }}>
          <VuiNumberFormat decimalScale={2} data={cell} prefix={cell > 0 ? '+' : ''} suffix={row.unit_name ? ` ${row.unit_name}` : ''} />
        </div>
      )
    },
    {
      dataField: 'qty_after',
      text: 'Qty After',
      render: (cell: any, row: any) => <VuiNumberFormat decimalScale={2} data={cell} suffix={row.unit_name ? ` ${row.unit_name}` : ''} />
    },
    {
      dataField: 'note',
      text: 'Note'
    }
  ];

  const changeTableLoading = (val: boolean) => {
    if (isMounted.current) {
      tableConfig.loading.set(val);
    }
  };

  const getTableData = async (reset: boolean = false, forNextPage: boolean = false) => {
    if (reset) tableConfig.page.set(1);
    if (isMounted.current && forNextPage) {
      setIsFetchingMoreData(true);
      tableConfig.page.set(tableConfig.page.get() + 1);
    } else {
      changeTableLoading(true);
    }

    if (isMounted.current) {
      setHasMore(true);
    }

    const conf = _.omit($clone(tableConfig.value), 'loading');

    const { date_from: dateFrom, date_to: dateTo, search } = defaultParams;
    let params: any = {
      ...conf,
      search: search ? search : null,
      date_from: moment(dateFrom).startOf('day').toISOString(),
      date_to: moment(dateTo).endOf('day').toISOString(),
      product_id: _.get(selectedProduct, 'id') || null,
      transaction_type: _.get(selectedType, 'id') || null
    };

    if (isMounted.current && !forNextPage) tableData.set([]);

    await ProductMutationLogRepository.all(params)
      .then((response: AxiosResponse) => {
        if (isMounted.current) {
          const { data: responseData } = response.data;
          tableData.merge(responseData);
          setIsFetchingMoreData(false);
          setHasInitialize(true);
          if (responseData.length < tableConfig.per_page.get()) {
            setHasMore(false);
          }
          if (reset) {
            setTotalData(_.get(response.data, 'meta.to') || responseData.length);
          }
        }
        changeTableLoading(false);
      })
      .catch((e: AxiosError) => {
        if (e && e.message !== 'all canceled') {
          if (e.isAxiosError) showToast(e.message, 'error');
          if (isMounted.current) {
            setHasInitialize(true);
            setIsFetchingMoreData(false);
            setTotalData(0);
          }
          changeTableLoading(false);
        }
      });
  };

  useMemo(() => {
    getTableData(true);
  }, [defaultParams, selectedProduct, selectedType]);

  useMemo(() => {
    if (isBottom && hasInitialize && !isFetchingMoreData && hasMore) {
      getTableData(false, true);
    }
  }, [isBottom]);

  return (
    <>
      <div className={'default-filter-section'}>
        <div className={'filter-item'}>
          <VuiSelect
            selectRepository={ProductRepository}
            defaultValue={selectedProduct}
            clearable={true}
            placeholder={'All Product'}
            onChange={(val: any) => setSelectedProduct(val)}
          />
        </div>

        <div className={'filter-item'}>
          <VuiSelect
            options={transactionTypeOptions}
            defaultValue={selectedType}
            clearable={true}
            placeholder={'All Type'}
            onChange={(val: any) => setSelectedType(val)}
          />
        </div>
      </div>

      <DataTableInfiniteScroll
        columns={tableColumns}
        totalDataTitle={'Total Log'}
        data={tableData.get()}
        propKey={'id'}
        isLoading={tableConfig.loading.get()}
        isFetchingMoreData={isFetchingMoreData}
        totalData={totalData}
      />
    </>
  );
};

export default TableProductLog;
